// Response shapes for the PHA-441 Smart Hub backend (PHA-469/470/471/479/510).
// Mirror the Python handlers in app_logic.py — keep in sync.
import { summaryHasItems } from '../../utils/ehrChanges';

export type SmartHubSourceKind = 'survey' | 'reading' | 'task' | 'ehr_change';

export interface SmartHubAnswerRow {
  source_kind: 'survey';
  submission_id: string;
  prompt: string;
  patient_id: string;
  patient_name: string;
  external_question_id?: string | null;
  value: unknown;
  submission_time: string | null;
  is_abnormal: boolean;
  priority?: number;
}

export interface SmartHubReadingRow {
  source_kind: 'reading';
  reading_id: number;
  reading_type?: string | null;
  patient_id: string;
  patient_name: string;
  device_id?: string | null;
  value: unknown;
  submission_time: string | null;
  is_abnormal: boolean;
  priority?: number;
}

export interface SmartHubTaskRow {
  source_kind: 'task';
  patient_id: string;
  patient_name: string;
  disease?: string | null;
  task: string;
  occurrence_id?: number | null;
  due_at: string | null;
  release_at: string | null;
  status?: string | null;
  is_overdue: boolean;
  priority?: number;
  // Frontend-only field set by the Smart Hub display dedup
  // (TaskBar.tsx). Counts how many distinct task_occurrence rows
  // collapsed into this card — one per care plan / disease that
  // produced the same task at the same due_at. The backend never
  // emits this; clinicians complete each occurrence individually
  // from the patient's Care Plans tab.
  duplicate_count?: number;
}

// PHA-510: one row per patient with EHR-driven changes pending clinician
// review. Server-side query filters acknowledged_at IS NULL and joins on
// the org-visibility template; the row carries everything the FE needs to
// render the bucket text and click through to CarePlanApproval.
//
// PHA-510 follow-up: change_summary gained two additional buckets
// (vitals_added, labs_added) once the daily sync started ingesting FHIR
// Observation resources. Each observation entry carries the FHIR LOINC
// alongside the value/unit/date so AckDrawer can render the actual reading.
export interface SmartHubEhrObservationItem {
  document_type: string;
  loinc: string | null;
  value: number | string | null;
  unit: string | null;
  date: string | null;
  label: string;
  applicability?: string;
}

export interface SmartHubEhrChangeItem {
  icd10: string | null;
  label: string;
  applicability?: string;
  record?: Record<string, any>;
  detected_at?: string | null;
  previous_dosage?: string | null;
}

export interface SmartHubEhrChangeRow {
  source_kind: 'ehr_change';
  ehr_sync_run_id: number;
  patient_id: string;
  patient_name: string;
  // 6-bucket diff payload — 4 from PHA-518 (problems/medications) + 2 from
  // the PHA-510 follow-up (vitals/labs). Older rows persisted before the
  // observation work landed will only carry the original four; renderers
  // must treat the new keys as optional.
  // PHA-1141: items also carry `record` (the full EHR row) and `detected_at`; dose changes
  // carry `previous_dosage`. Two medication buckets were added. The server drops items already
  // decided in the chart, so a partially reviewed run shows only what is left.
  change_summary: {
    problems_added?: SmartHubEhrChangeItem[];
    problems_resolved?: SmartHubEhrChangeItem[];
    meds_added?: SmartHubEhrChangeItem[];
    meds_changed?: SmartHubEhrChangeItem[];
    meds_dosage_changed?: SmartHubEhrChangeItem[];
    meds_removed?: SmartHubEhrChangeItem[];
    vitals_added?: SmartHubEhrObservationItem[];
    labs_added?: SmartHubEhrObservationItem[];
  };
  applicability_summary: { applicable_count?: number; non_applicable_count?: number };
  // Patient_id when the LLM regen produced a draft set; null when
  // status='no_changes_applicable' (EHR moved but nothing applicable to
  // the patient's care plan changed). The FE uses this to decide between
  // "navigate to approval" vs "show AckDrawer for explicit dismissal".
  draft_care_plan_id: string | null;
  status: 'changes_found' | 'no_changes_applicable';
  // Date of the previous snapshot the diff was computed against — rendered
  // as the trailing "since YYYY-MM-DD" in the row text. Null on first sync.
  since_date: string | null;
  started_at: string | null;
  priority?: number;
}

export type SmartHubItem =
  | SmartHubAnswerRow
  | SmartHubReadingRow
  | SmartHubTaskRow
  | SmartHubEhrChangeRow;

// PHA-678: item descriptor for the bulk acknowledge endpoint.
export type BulkAckItem =
  | { kind: 'reading'; reading_id: number }
  | { kind: 'answer'; submission_id: string; prompt: string }
  | { kind: 'ehr_change'; ehr_sync_run_id: number };

// PHA-678: a per-patient slice of a selection. The global selection bar can
// span many patients; acknowledging fires one bulk call per group (one
// 60-second billing event per patient).
export interface BulkAckGroup {
  patientId: string;
  patientName: string;
  items: SmartHubItem[];
}

// PHA-1141 / PHA-1142: an EHR change with itemised problems, medications or readings is
// decided item by item in the chart (accepting one updates the chart's list), so it is not
// bulk-acknowledgeable: acknowledging the whole run from here would clear it with nothing
// reviewed and nothing applied. Only a run with nothing itemised keeps the checkbox.
const ehrChangeIsItemized = (item: SmartHubEhrChangeRow): boolean =>
  summaryHasItems(item.change_summary as any);

// Stable key for checkbox state. Must uniquely identify an item within a feed.
export const itemKey = (item: SmartHubItem): string | null => {
  if (isReading(item)) return `reading:${item.reading_id}`;
  if (isAnswer(item)) return `answer:${item.submission_id}:${item.prompt}`;
  if (isEhrChange(item)) return ehrChangeIsItemized(item) ? null : `ehr_change:${item.ehr_sync_run_id}`;
  return null; // tasks are not acknowledgeable
};

// Convert a SmartHubItem to a BulkAckItem for the bulk endpoint.
export const toBulkAckItem = (item: SmartHubItem): BulkAckItem | null => {
  if (isReading(item)) return { kind: 'reading', reading_id: item.reading_id };
  if (isAnswer(item)) return { kind: 'answer', submission_id: item.submission_id, prompt: item.prompt };
  if (isEhrChange(item)) {
    return ehrChangeIsItemized(item) ? null : { kind: 'ehr_change', ehr_sync_run_id: item.ehr_sync_run_id };
  }
  return null;
};

// PHA-479: synthetic group used by AckDrawer when the user clicks
// "Acknowledge all" on a survey submission. Not part of the server feed.
export interface SmartHubSubmissionGroup {
  source_kind: 'submission';
  submission_id: string;
  patient_id: string;
  patient_name: string;
  submission_time: string | null;
  answers: SmartHubAnswerRow[];
}

export interface TaskBarFeed {
  todo: SmartHubItem[];
  // PHA-479: red BP/glucose + invalid readings (survey + telli rows mixed).
  out_of_bounds_vital_signs: (SmartHubAnswerRow | SmartHubReadingRow)[];
  hospitalizations: SmartHubAnswerRow[];
  // PHA-510: EHR-driven changes pending clinician review. Rendered above
  // overdue_upcoming_tasks (highest clinical urgency, per parent issue).
  ehr_changes_pending: SmartHubEhrChangeRow[];
  overdue_upcoming_tasks: SmartHubTaskRow[];
  // PHA-479: in-bounds BP / glucose only (survey + telli rows mixed).
  new_vital_signs: (SmartHubAnswerRow | SmartHubReadingRow)[];
  // PHA-479: non-vital, non-hospitalization survey answers — grouped by
  // submission_id on render so one survey response counts as 1.
  new_patient_answers: SmartHubAnswerRow[];
  new_device_readings: SmartHubReadingRow[];
}

export interface BillingGapsFeed {
  ccm_gap_to_20: Array<{
    patient_id: string;
    patient_name: string;
    spent_minutes: number;
    gap_minutes: number;
    target_minutes?: number;
  }>;
  // PHA-1144: RPM patients short of the 10 min management threshold (99470).
  // Optional so an older backend (deploy skew) still type-checks; read with ?? [].
  rpm_minutes_to_10?: Array<{
    patient_id: string;
    patient_name: string;
    spent_minutes: number;
    gap_minutes: number;
    target_minutes?: number;
  }>;
  apcm_no_activity_this_month: Array<{
    patient_id: string;
    patient_name: string;
    apcm_tier: number | null;
  }>;
  rpm_days_short: Array<{
    patient_id: string;
    patient_name: string;
    days_with_measurements: number;
    required_days: number;
    cycle_day?: number;
  }>;
}

// PHA-567 SmartHub AI — patient intelligence layer types.
// Mirrors backend/app_server/smart_hub_ai/ schema. Sparkline points are
// DETERMINISTIC (computed in trends.py); the LLM never emits them. The FE
// looks them up by sparkline_series_id from PatientAiInsight.sparkline_series.
export type AiInsightTier = 'critical' | 'watch' | 'stable';
export type AiInsightUrgency = 'today' | 'this_week' | 'this_month';
export type AiInsightCitationKind =
  | 'reading'
  | 'survey'
  | 'ehr_change'
  | 'call'
  | 'task'
  | 'lab';
export type AiInsightLiveSignal = 'out_of_bounds' | 'hospitalization';

export interface AiInsightTrendCallout {
  metric: 'systolic' | 'diastolic' | 'bg' | 'weight' | 'spo2';
  window_days: number;
  direction: 'rising' | 'falling' | 'flat' | 'volatile' | 'insufficient_data';
  slope_per_week: number;
  variability_cv: number;
  out_of_range_pct: number;
  data_gap_days: number | null;
  current_value: number | null;
  baseline_value: number | null;
  sparkline_series_id: string;
  // narrative is the LLM-produced single-sentence interpretation of the
  // deterministic observation. NEVER points — those live on
  // PatientAiInsight.sparkline_series, keyed by sparkline_series_id.
  narrative: string;
  n_points?: number;
}

export interface AiInsightAction {
  summary: string;
  steps?: string[];
  urgency: AiInsightUrgency;
  // ma_action only:
  suggested_call_script?: string;
  // md_action only — rendered as INERT chips that open the source via
  // citation deep-link (PHA-575). Never auto-triggers anything.
  suggested_orders?: string[];
}

export interface AiInsightCitation {
  kind: AiInsightCitationKind;
  ref_id: string;
  snippet: string;
}

export interface PatientAiInsight {
  insight_id: number;
  patient_id: string;
  patient_name?: string | null;
  risk_tier: AiInsightTier;
  risk_score: number;
  why_now: string;
  trend_callouts: AiInsightTrendCallout[];
  // Keyed by sparkline_series_id; values are the deterministic series the
  // FE renders. Populated by trends.py on the backend.
  sparkline_series: Record<string, number[]>;
  ma_action: AiInsightAction;
  md_action: AiInsightAction | null;
  citations: AiInsightCitation[];
  computed_at: string;
  prompt_version: string;
  model_id?: string;
  trigger_source?: string;
  // PHA-574: present when the rule-based feed currently has an acute
  // alert for this patient, so the FE bumps to the top with a "● LIVE" pip.
  // Backend overlays this at request time; never persisted.
  live_signal?: AiInsightLiveSignal;
}

export interface AiInsightsListResponse {
  enabled: boolean;
  insights: PatientAiInsight[];
}

export interface AiInsightDetailResponse {
  insight: PatientAiInsight | null;
  history?: PatientAiInsight[];
}

// Type guards
export const isAnswer = (i: SmartHubItem): i is SmartHubAnswerRow =>
  i.source_kind === 'survey';
export const isReading = (i: SmartHubItem): i is SmartHubReadingRow =>
  i.source_kind === 'reading';
export const isTask = (i: SmartHubItem): i is SmartHubTaskRow =>
  i.source_kind === 'task';
export const isEhrChange = (i: SmartHubItem): i is SmartHubEhrChangeRow =>
  i.source_kind === 'ehr_change';
